import { InferenceSession, Tensor, env, type TypedTensor } from 'onnxruntime-web'
// @ts-ignore
import npyjs from 'npyjs'
import type { BaClickInput, OnnxModelData } from '../../typings/ai'

class OnnxHelper {
  static async createInferenceSession(url: string): Promise<InferenceSession> {
    env.wasm.wasmPaths = import.meta.env.VITE_WASM_URL
    const modelUrl = url
    const model = await InferenceSession.create(modelUrl)
    return model
  }

  static async loadTensor(file: string): Promise<TypedTensor<'float32'> | null> {
    const npLoader = new npyjs()
    try {
      const npArray = await npLoader.load(file)
      const tensor = new Tensor('float32', npArray.data, npArray.shape)
      return tensor
    } catch (error) {
      console.warn('Error loading tensor:', error)
      return null
    }
  }

  static prepareOnnxModelFeeds({ clicks, tensor, modelScale }: OnnxModelData) {
    const imageEmbedding = tensor
    let pointCoords
    let pointLabels
    let pointCoordsTensor
    let pointLabelsTensor

    // Check there are input click prompts
    if (clicks) {
      const n = clicks.length

      // If there is no box input, a single padding point with
      // label -1 and coordinates (0.0, 0.0) should be concatenated
      // so initialize the array to support (n + 1) points.
      pointCoords = new Float32Array(2 * (n + 1))
      pointLabels = new Float32Array(n + 1)

      // Add clicks and scale to what SAM expects
      for (let i = 0; i < n; i++) {
        pointCoords[2 * i] = clicks[i].x / modelScale.onnxScale
        pointCoords[2 * i + 1] = clicks[i].y / modelScale.onnxScale
        pointLabels[i] = clicks[i].clickType
      }

      // Add in the extra point/label when only clicks and no box
      // The extra point is at (0, 0) with label -1
      pointCoords[2 * n] = 0.0
      pointCoords[2 * n + 1] = 0.0
      pointLabels[n] = -1.0

      // Create the tensor
      pointCoordsTensor = new Tensor('float32', pointCoords, [1, n + 1, 2])
      pointLabelsTensor = new Tensor('float32', pointLabels, [1, n + 1])
    }
    const imageSizeTensor = new Tensor('float32', [modelScale.height, modelScale.width])

    if (pointCoordsTensor === undefined || pointLabelsTensor === undefined) return

    // const hasPrevPredMask = prevPredMask && clicks && !OnnxHelper._isFirstClick(clicks);

    // if there is a previous tensor, use it, otherwise we default to an empty tensor
    // const maskInput = hasPrevPredMask
    //   ? prevPredMask
    //   : new Tensor('float32', new Float32Array(256 * 256), [1, 1, 256, 256]);
    const maskInput = new Tensor('float32', new Float32Array(256 * 256), [1, 1, 256, 256])
    // There is no previous mask, so default to 0
    // const hasMaskInput = new Tensor('float32', [hasPrevPredMask ? 1 : 0]);
    const hasMaskInput = new Tensor('float32', [0])

    return {
      image_embeddings: imageEmbedding,
      point_coords: pointCoordsTensor,
      point_labels: pointLabelsTensor,
      orig_im_size: imageSizeTensor,
      mask_input: maskInput,
      has_mask_input: hasMaskInput
    }
  }

  static handleImageScale(opts: { width: number; height: number }) {
    const { width, height } = opts

    const IMAGE_SIZE = 500
    const UPLOAD_IMAGE_SIZE = 1024
    let scale
    let uploadScale
    if (height < width) {
      scale = IMAGE_SIZE / height
      if (height * scale > 1333) {
        scale = 1333 / height
      }
      uploadScale = UPLOAD_IMAGE_SIZE / width
    } else {
      scale = IMAGE_SIZE / width
      if (width * scale > 1333) {
        scale = 1333 / width
      }
      uploadScale = UPLOAD_IMAGE_SIZE / height
    }
    return { height, width, scale, uploadScale }
  }

  static inverseOnnxMask(input: Float32Array): Float32Array {
    const arr = input.slice()
    for (let i = 0; i < arr.length; i++) {
      arr[i] = arr[i] > 0.0 ? 0.0 : 1.0
    }

    return arr
  }

  static onnxMaskToImage(options: {
    input: any
    width: number
    height: number
    maskColor: Array<number>
    bgColor?: Array<number>
  }) {
    return OnnxHelper._imageDataToImage(OnnxHelper._arrayToImageData(options))
  }

  private static _arrayToImageData(options: {
    input: any
    width: number
    height: number
    maskColor: Array<number>
    bgColor?: Array<number>
  }) {
    const { input, width, height, maskColor, bgColor } = options

    const [r, g, b, a] = maskColor // the masks's blue color
    const [bgR, bgG, bgB, bgA] = bgColor ?? [0, 0, 0, 0]

    const arr = new Uint8ClampedArray(4 * width * height).fill(0)
    for (let i = 0; i < input.length; i++) {
      // Threshold the onnx model mask prediction at 0.0
      // This is equivalent to thresholding the mask using predictor.model.mask_threshold
      // in python
      if (input[i] > 0.0) {
        arr[4 * i + 0] = r
        arr[4 * i + 1] = g
        arr[4 * i + 2] = b
        arr[4 * i + 3] = a
      } else if (bgColor) {
        arr[4 * i + 0] = bgR
        arr[4 * i + 1] = bgG
        arr[4 * i + 2] = bgB
        arr[4 * i + 3] = bgA
      }
    }
    return new ImageData(arr, width, height)
  }

  private static _imageDataToImage(imageData: ImageData) {
    const canvas = OnnxHelper._imageDataToCanvas(imageData)
    const image = new Image()
    image.src = canvas.toDataURL()
    return image
  }

  private static _imageDataToCanvas(imageData: ImageData) {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    canvas.width = imageData.width
    canvas.height = imageData.height
    ctx?.putImageData(imageData, 0, 0)
    return canvas
  }

  private static _isFirstClick(clicks: Array<BaClickInput>) {
    return (
      (clicks.length === 1 && (clicks[0].clickType === 1 || clicks[0].clickType === 0)) ||
      (clicks.length === 2 && clicks.every((c) => c.clickType === 2 || c.clickType === 3))
    )
  }
}

export default OnnxHelper
